import React from "react";
import {
    Card,
    CardContent,
} from "@/components/ui/Card"
import { getSectionRiskColorKey, SECTION_RISK_COLOR_PALETTE } from "@/services/sectionRiskService";

const ReportSummaryTab = ({
    inherentRiskRows: inherentRiskSourceRows = [],
    controlEfficacyRows: controlEfficacySourceRows = [],
    sectionRiskRows: sectionRiskSourceRows = [],
}) => {
    const colorPalette = SECTION_RISK_COLOR_PALETTE

    const getTextColorClass = (colorKey) => {
        const whiteTextColors = ["red", "green", "blue"]
        return whiteTextColors.includes(colorKey) ? "text-gray-0" : "text-gray-900"
    }

    const getInherentRiskColorKey = (label) => {
        if (!label) return null
        const normalized = String(label).toLowerCase()
        if (normalized === "Severe") return "red"
        if (normalized === "major") return "orange"
        if (normalized === "moderate") return "yellow"
        if (normalized === "minor") return "green"
        if (normalized === "insignificant") return "blue"
        return null
    }

    const getControlBandColorKey = (value) => {
        if (value === null || value === undefined || value === "") return null
        const numeric = Number(value)
        if (Number.isNaN(numeric)) return null
        if (numeric >= 9) return "blue"
        if (numeric >= 7) return "green"
        if (numeric >= 5) return "yellow"
        if (numeric >= 3) return "orange"
        if (numeric >= 0) return "red"
        return null
    }

    const getPdScoreColorKey = (score) => {
        const value = Number(score)
        if (value === 1) return "blue"
        if (value === 2) return "green"
        if (value === 3) return "yellow"
        if (value === 4) return "orange"
        if (value === 5) return "red"
        return null
    }

    const getBiScoreColorKey = (score) => {
        const value = Number(score)
        if (value === 1) return "blue"
        if (value === 3) return "green"
        if (value === 9) return "yellow"
        if (value === 27) return "orange"
        if (value === 81) return "red"
        return null
    }

    /** Matches chartGenerator likelihood box fill bands (last filled pair color). */
    const LIKELIHOOD_FILL_COUNTS = {
        certain: 2,
        likely: 4,
        possible: 6,
        unlikely: 8,
        rare: 10,
    }

    const LIKELIHOOD_BAND_COLOR_KEYS = [
        "red",
        "red",
        "orange",
        "orange",
        "yellow",
        "yellow",
        "green",
        "green",
        "blue",
        "blue",
    ]

    const getLikelihoodColorKey = (likelihood) => {
        const key = String(likelihood ?? "").trim().toLowerCase()
        const fillCount = LIKELIHOOD_FILL_COUNTS[key]
        if (!fillCount) return null
        return LIKELIHOOD_BAND_COLOR_KEYS[fillCount - 1] ?? null
    }

    const getSectionRiskScoreColorKey = (score, hasValidInherent = true) => getSectionRiskColorKey(score, hasValidInherent)

    const inherentRiskRows = React.useMemo(() => {
        if (!Array.isArray(inherentRiskSourceRows)) return []

        // 1. Filter out completely empty rows
        const filteredRows = inherentRiskSourceRows.filter((row) => {
            const riskName = (row.risk || "").trim()
            const categoryName = (row.category || "").trim()
            return riskName !== "" && categoryName !== ""
        })

        // 2. Group rows by risk name while preserving the order
        const groups = new Map()
        const riskOrder = []

        filteredRows.forEach((row) => {
            const riskName = (row.risk || "").trim()
            const key = riskName || "__no-risk__"

            if (!groups.has(key)) {
                groups.set(key, [])
                riskOrder.push(key)
            }
            groups.get(key).push(row)
        })

        // 3. Flatten groups so each risk appears once and all its rows stay together
        const result = []

        riskOrder.forEach((key) => {
            const rows = groups.get(key) || []
            rows.forEach((row, index) => {
                const riskName = (row.risk || "").trim()
                const categoryName = (row.category || "").trim()

                const pdScore = row.pd ?? row.pdScore ?? ""
                const biScore = row.bi ?? row.biScore ?? ""
                const pdBi = row.pdBi ?? ""
                const consequence = row.consequence ?? ""
                const likelihood = row.likelihood ?? ""
                const inherentRisk = row.inherentRisk ?? ""
                const inherentRiskScore = row.score ?? row.inherentRiskScore ?? ""

                const colorKey = getInherentRiskColorKey(inherentRisk)

                result.push({
                    risk: index === 0 ? riskName : "",
                    category: categoryName,
                    pdScore,
                    biScore,
                    pdBi,
                    consequence,
                    likelihood,
                    inherentRisk,
                    inherentRiskScore,
                    colorKey,
                })
            })
        })

        return result
    }, [inherentRiskSourceRows])

    const controlEfficacyRows = React.useMemo(() => {
        if (!Array.isArray(controlEfficacySourceRows)) return []

        const filteredRows = controlEfficacySourceRows.filter((row) => {
            const riskName = (row.risk || "").trim()
            const categoryName = (row.category || "").trim()
            return riskName !== "" && categoryName !== ""
        })

        const groups = new Map()
        const riskOrder = []

        filteredRows.forEach((row) => {
            const riskName = (row.risk || "").trim()
            const key = riskName || "__no-risk__"

            if (!groups.has(key)) {
                groups.set(key, [])
                riskOrder.push(key)
            }
            groups.get(key).push(row)
        })

        const result = []

        riskOrder.forEach((key) => {
            const rows = groups.get(key) || []
            rows.forEach((row, index) => {
                const riskName = (row.risk || "").trim()
                const categoryName = (row.category || "").trim()

                result.push({
                    risk: index === 0 ? riskName : "",
                    category: categoryName,
                    design: row.design ?? "",
                    implementation: row.implementation ?? "",
                    controlEfficacy: row.controlEfficacy ?? "",
                })
            })
        })

        return result
    }, [controlEfficacySourceRows])

    const sectionRiskRows = React.useMemo(() => {
        if (!Array.isArray(sectionRiskSourceRows)) return []

        const filteredRows = sectionRiskSourceRows.filter((row) => {
            const riskName = (row.risk || "").trim()
            const categoryName = (row.category || "").trim()
            return riskName !== "" && categoryName !== ""
        })

        const groups = new Map()
        const riskOrder = []

        filteredRows.forEach((row) => {
            const riskName = (row.risk || "").trim()
            const key = riskName || "__no-risk__"

            if (!groups.has(key)) {
                groups.set(key, [])
                riskOrder.push(key)
            }
            groups.get(key).push(row)
        })

        const result = []

        riskOrder.forEach((key) => {
            const rows = groups.get(key) || []
            rows.forEach((row, index) => {
                const riskName = (row.risk || "").trim()
                const categoryName = (row.category || "").trim()

                result.push({
                    risk: index === 0 ? riskName : "",
                    category: categoryName,
                    inherentRiskScore: row.inherentRiskScore ?? "",
                    controlEfficacy: row.controlEfficacy ?? "",
                    riskScore: row.riskScore ?? "",
                })
            })
        })

        return result
    }, [sectionRiskSourceRows])

    const graphDataRows = React.useMemo(() => {
        if (!Array.isArray(sectionRiskSourceRows)) return []

        const groups = new Map()

        sectionRiskSourceRows.forEach((row) => {
            const riskName = (row.risk || "").trim()
            if (!riskName) return
            if (!groups.has(riskName)) {
                groups.set(riskName, [])
            }
            groups.get(riskName).push(row)
        })

        const parseValidNumber = (value) => {
            if (value === null || value === undefined) return null
            const stringValue = String(value).trim()
            if (stringValue === "") return null
            const numeric = Number(stringValue)
            if (Number.isNaN(numeric)) return null
            return numeric
        }

        const average = (values) => {
            if (!values.length) return ""
            const sum = values.reduce((acc, v) => acc + v, 0)
            const avg = sum / values.length
            return Number.isFinite(avg) ? avg.toFixed(1) : ""
        }

        const result = []

        groups.forEach((rows, riskName) => {
            const inherentValues = []
            const controlValues = []
            const riskScoreValues = []

            rows.forEach((row) => {
                const inherent = parseValidNumber(row.inherentRiskScore)
                if (inherent !== null) inherentValues.push(inherent)

                const control = parseValidNumber(row.controlEfficacy)
                if (control !== null) controlValues.push(control)

                const riskScore = parseValidNumber(row.riskScore)
                if (riskScore !== null) riskScoreValues.push(riskScore)
            })

            result.push({
                risk: riskName,
                inherent: average(inherentValues),
                efficacy: average(controlValues),
                percent: average(riskScoreValues),
            })
        })

        return result
    }, [sectionRiskSourceRows])

    const mineOverallRow = React.useMemo(() => {
        if (!graphDataRows.length) return null

        const parseValidNumber = (value) => {
            if (value === null || value === undefined) return null
            const stringValue = String(value).trim()
            if (stringValue === "") return null
            const numeric = Number(stringValue)
            if (Number.isNaN(numeric)) return null
            return numeric
        }

        const average = (values) => {
            if (!values.length) return ""
            const sum = values.reduce((acc, v) => acc + v, 0)
            const avg = sum / values.length
            return Number.isFinite(avg) ? avg.toFixed(1) : ""
        }

        const inherentValues = []
        const efficacyValues = []
        const percentValues = []

        graphDataRows.forEach((row) => {
            const inherent = parseValidNumber(row.inherent)
            if (inherent !== null) inherentValues.push(inherent)

            const efficacy = parseValidNumber(row.efficacy)
            if (efficacy !== null) efficacyValues.push(efficacy)

            const percent = parseValidNumber(row.percent)
            if (percent !== null) percentValues.push(percent)
        })

        return {
            risk: "Mine Overall",
            inherent: average(inherentValues),
            efficacy: average(efficacyValues),
            percent: average(percentValues),
        }
    }, [graphDataRows])

    return (
        <div className="py-4 space-y-6 slide-up">

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <Card className="border-none rounded-md shadow-none bg-navy-100">
                    <CardContent className="p-6">
                        <h5 className="mb-4 font-semibold uppercase text-gray-0">Property Damage (USD)</h5>
                        <div className="space-y-3">
                            <div className="flex items-center gap-3 p-3 rounded bg-navy-500">
                                <div className="w-8 h-8 rounded" style={{ backgroundColor: colorPalette.blue }}></div>
                                <div className="flex-1">
                                    <span className="text-sm text-gray-0">&lt; USD500k</span>
                                    <span className="ml-3 text-sm text-gray-400">(Fixed Value: 1)</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-3 rounded bg-navy-500">
                                <div className="w-8 h-8 rounded" style={{ backgroundColor: colorPalette.green }}></div>
                                <div className="flex-1">
                                    <span className="text-sm text-gray-0">&gt; USD500k to USD5.0M</span>
                                    <span className="ml-3 text-sm text-gray-400">(Fixed Value: 2)</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-3 rounded bg-navy-500">
                                <div className="w-8 h-8 rounded" style={{ backgroundColor: colorPalette.yellow }}></div>
                                <div className="flex-1">
                                    <span className="text-sm text-gray-0">&gt; USD5.0M to USD10.0M</span>
                                    <span className="ml-3 text-sm text-gray-400">(Fixed Value: 3)</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-3 rounded bg-navy-500">
                                <div className="w-8 h-8 rounded" style={{ backgroundColor: colorPalette.orange }}></div>
                                <div className="flex-1">
                                    <span className="text-sm text-gray-0">&gt; USD10.0M to USD50.0M</span>
                                    <span className="ml-3 text-sm text-gray-400">(Fixed Value: 4)</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-3 rounded bg-navy-500">
                                <div className="w-8 h-8 rounded" style={{ backgroundColor: colorPalette.red }}></div>
                                <div className="flex-1">
                                    <span className="text-sm text-gray-0">&gt; USD50M</span>
                                    <span className="ml-3 text-sm text-gray-400">(Fixed Value: 5)</span>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-none rounded-md shadow-none bg-navy-100">
                    <CardContent className="p-6">
                        <h5 className="mb-4 font-semibold uppercase text-gray-0">Business Interruption</h5>
                        <div className="space-y-3">
                            <div className="flex items-center gap-3 p-3 rounded bg-navy-500">
                                <div className="w-8 h-8 rounded" style={{ backgroundColor: colorPalette.blue }}></div>
                                <div className="flex-1">
                                    <span className="text-sm text-gray-0">&lt;= 30 Days</span>
                                    <span className="ml-3 text-sm text-gray-400">(Fixed Value: 1)</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-3 rounded bg-navy-500">
                                <div className="w-8 h-8 rounded" style={{ backgroundColor: colorPalette.green }}></div>
                                <div className="flex-1">
                                    <span className="text-sm text-gray-0">&gt; 30 Days to 90 Days</span>
                                    <span className="ml-3 text-sm text-gray-400">(Fixed Value: 3)</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-3 rounded bg-navy-500">
                                <div className="w-8 h-8 rounded" style={{ backgroundColor: colorPalette.yellow }}></div>
                                <div className="flex-1">
                                    <span className="text-sm text-gray-0">&gt; 90 Days to 180 Days</span>
                                    <span className="ml-3 text-sm text-gray-400">(Fixed Value: 9)</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-3 rounded bg-navy-500">
                                <div className="w-8 h-8 rounded" style={{ backgroundColor: colorPalette.orange }}></div>
                                <div className="flex-1">
                                    <span className="text-sm text-gray-0">&gt; 180 Days to 12 Months</span>
                                    <span className="ml-3 text-sm text-gray-400">(Fixed Value: 27)</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-3 rounded bg-navy-500">
                                <div className="w-8 h-8 rounded" style={{ backgroundColor: colorPalette.red }}></div>
                                <div className="flex-1">
                                    <span className="text-sm text-gray-0">&gt; 12 Months</span>
                                    <span className="ml-3 text-sm text-gray-400">(Fixed Value: 81)</span>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            <Card className="border-none rounded-md shadow-none bg-navy-100">
                <CardContent className="p-6">
                    <h5 className="mb-4 font-semibold uppercase text-gray-0">
                        Inherent Risk Tab <span className="text-sm font-normal text-gray-400">(values from Inherent Risk tab)</span>
                    </h5>
                    <div className="mt-4 overflow-x-auto">
                        <table className="w-full text-sm border-collapse">
                            <thead>
                                <tr className="border-b border-gray-600">
                                    <th className="px-3 py-2 font-semibold text-left uppercase border border-gray-600 bg-navy-500 text-gray-0">
                                        Risk Section
                                    </th>
                                    <th className="px-3 py-2 font-semibold text-left uppercase border border-gray-600 bg-navy-500 text-gray-0">
                                        Risk Sub-Section
                                    </th>
                                    <th className="px-3 py-2 font-semibold text-center uppercase border border-gray-600 bg-navy-500 text-gray-0">
                                        PD Score
                                    </th>
                                    <th className="px-3 py-2 font-semibold text-center uppercase border border-gray-600 bg-navy-500 text-gray-0">
                                        BI Score
                                    </th>
                                    <th className="px-3 py-2 font-semibold text-center uppercase border border-gray-600 bg-navy-500 text-gray-0">
                                        PD*BI
                                    </th>
                                    <th className="px-3 py-2 font-semibold text-left uppercase border border-gray-600 bg-navy-500 text-gray-0">
                                        Consequence
                                    </th>
                                    <th className="px-3 py-2 font-semibold text-left uppercase border border-gray-600 bg-navy-500 text-gray-0">
                                        Likelihood
                                    </th>
                                    <th className="px-3 py-2 font-semibold text-left uppercase border border-gray-600 bg-navy-500 text-gray-0">
                                        Inherent Risk
                                    </th>
                                    <th className="px-3 py-2 font-semibold text-center uppercase border border-gray-600 bg-navy-500 text-gray-0">
                                        Inherent Risk Score
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {inherentRiskRows.map((row, index) => {
                                    const hasColor = row.colorKey
                                    const scoreBgColor = hasColor ? colorPalette[row.colorKey] : null
                                    const scoreTextClass = hasColor ? getTextColorClass(row.colorKey) : "text-gray-0"

                                    const pdColorKey = getPdScoreColorKey(row.pdScore)
                                    const biColorKey = getBiScoreColorKey(row.biScore)
                                    const likelihoodColorKey = getLikelihoodColorKey(row.likelihood)
                                    const pdBgColor = pdColorKey ? colorPalette[pdColorKey] : null
                                    const biBgColor = biColorKey ? colorPalette[biColorKey] : null
                                    const likelihoodBgColor = likelihoodColorKey
                                        ? colorPalette[likelihoodColorKey]
                                        : null
                                    const pdTextClass = pdColorKey ? getTextColorClass(pdColorKey) : "text-gray-0"
                                    const biTextClass = biColorKey ? getTextColorClass(biColorKey) : "text-gray-0"
                                    const likelihoodTextClass = likelihoodColorKey
                                        ? getTextColorClass(likelihoodColorKey)
                                        : "text-gray-0"

                                    return (
                                        <tr key={`${row.category}-${index}`} className="border-b border-gray-600 last:border-b-0">
                                            <td className="px-3 py-2 font-medium text-left uppercase border border-gray-600 bg-navy-500 text-gray-0">
                                                {row.risk}
                                            </td>
                                            <td className="px-3 py-2 text-left border border-gray-600 text-gray-0 bg-navy-500/40">
                                                {row.category}
                                            </td>
                                            <td
                                                className={`px-3 py-2 text-center border border-gray-600 bg-navy-500/40 ${pdTextClass}`}
                                                style={{ backgroundColor: pdBgColor || undefined }}
                                            >
                                                {row.pdScore}
                                            </td>
                                            <td
                                                className={`px-3 py-2 text-center border border-gray-600 bg-navy-500/40 ${biTextClass}`}
                                                style={{ backgroundColor: biBgColor || undefined }}
                                            >
                                                {row.biScore}
                                            </td>
                                            <td className="px-3 py-2 text-center border border-gray-600 bg-navy-500/40 text-gray-0">
                                                {row.pdBi}
                                            </td>
                                            <td className="px-3 py-2 text-left border border-gray-600 bg-navy-500/40 text-gray-0">
                                                {row.consequence}
                                            </td>
                                            <td
                                                className={`px-3 py-2 text-left border border-gray-600 bg-navy-500/40 ${likelihoodTextClass}`}
                                                style={{ backgroundColor: likelihoodBgColor || undefined }}
                                            >
                                                {row.likelihood}
                                            </td>
                                            <td
                                                className={`px-3 py-2 text-left border border-gray-600 bg-navy-500/40 ${scoreTextClass}`}
                                                style={{ backgroundColor: scoreBgColor || undefined }}
                                            >
                                                {row.inherentRisk}
                                            </td>
                                            <td
                                                className={`px-3 py-2 text-center border border-gray-600 font-semibold bg-navy-500/40 ${scoreTextClass}`}
                                                style={{ backgroundColor: scoreBgColor || undefined }}
                                            >
                                                {row.inherentRiskScore}
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>

            <Card className="border-none rounded-md shadow-none bg-navy-100">
                <CardContent className="p-6">
                    <h5 className="mb-4 font-semibold uppercase text-gray-0">
                        Control Efficacy Tab <span className="text-sm font-normal text-gray-400">(values from Control Efficacy tab)</span>
                    </h5>
                    <div className="mt-4 overflow-x-auto">
                        <table className="w-full text-sm border-collapse">
                            <thead>
                                <tr className="border-b border-gray-600">
                                    <th className="px-3 py-2 font-semibold text-left uppercase border border-gray-600 bg-navy-500 text-gray-0">
                                        Risk Section
                                    </th>
                                    <th className="px-3 py-2 font-semibold text-left uppercase border border-gray-600 bg-navy-500 text-gray-0">
                                        Risk Sub-Section
                                    </th>
                                    <th className="px-3 py-2 font-semibold text-center uppercase border border-gray-600 bg-navy-500 text-gray-0">
                                        Design
                                    </th>
                                    <th className="px-3 py-2 font-semibold text-center uppercase border border-gray-600 bg-navy-500 text-gray-0">
                                        Implementation
                                    </th>
                                    <th className="px-3 py-2 font-semibold text-center uppercase border border-gray-600 bg-navy-500 text-gray-0">
                                        Control Efficacy
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {controlEfficacyRows.map((row, index) => {
                                    const designColorKey = getControlBandColorKey(row.design)
                                    const implementationColorKey = getControlBandColorKey(row.implementation)
                                    const controlColorKey = getControlBandColorKey(row.controlEfficacy)

                                    const designBgColor = designColorKey ? colorPalette[designColorKey] : null
                                    const implementationBgColor = implementationColorKey ? colorPalette[implementationColorKey] : null
                                    const controlBgColor = controlColorKey ? colorPalette[controlColorKey] : null

                                    const designTextClass = designColorKey ? getTextColorClass(designColorKey) : "text-gray-0"
                                    const implementationTextClass = implementationColorKey ? getTextColorClass(implementationColorKey) : "text-gray-0"
                                    const controlTextClass = controlColorKey ? getTextColorClass(controlColorKey) : "text-gray-0"

                                    return (
                                        <tr key={`${row.category}-${index}`} className="border-b border-gray-600 last:border-b-0">
                                            <td className="px-3 py-2 font-medium text-left uppercase border border-gray-600 bg-navy-500 text-gray-0">
                                                {row.risk}
                                            </td>
                                            <td className="px-3 py-2 text-left border border-gray-600 text-gray-0 bg-navy-500/40">
                                                {row.category}
                                            </td>
                                            <td
                                                className={`px-3 py-2 text-center border border-gray-600 bg-navy-500/40 ${designTextClass}`}
                                                style={{ backgroundColor: designBgColor || undefined }}
                                            >
                                                {row.design}
                                            </td>
                                            <td
                                                className={`px-3 py-2 text-center border border-gray-600 bg-navy-500/40 ${implementationTextClass}`}
                                                style={{ backgroundColor: implementationBgColor || undefined }}
                                            >
                                                {row.implementation}
                                            </td>
                                            <td
                                                className={`px-3 py-2 text-center border border-gray-600 bg-navy-500/40 ${controlTextClass}`}
                                                style={{ backgroundColor: controlBgColor || undefined }}
                                            >
                                                {row.controlEfficacy}
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>

            <Card className="border-none rounded-md shadow-none bg-navy-100">
                <CardContent className="p-6">
                    <h5 className="mb-4 text-lg font-semibold uppercase text-gray-0">
                        Section Risk Scores Tab <span className="text-sm font-normal text-gray-400">(values from Section Risk Scores tab)</span>
                    </h5>
                    <div className="mt-4 overflow-x-auto">
                        <table className="w-full text-sm border-collapse">
                            <thead>
                                <tr className="border-b border-gray-600">
                                    <th className="px-3 py-2 font-semibold text-left uppercase border border-gray-600 bg-navy-500 text-gray-0">
                                        Risk Section
                                    </th>
                                    <th className="px-3 py-2 font-semibold text-left uppercase border border-gray-600 bg-navy-500 text-gray-0">
                                        Risk Sub-Section
                                    </th>
                                    <th className="px-3 py-2 font-semibold text-center uppercase border border-gray-600 bg-navy-500 text-gray-0">
                                        Inherent Risk Score
                                    </th>
                                    <th className="px-3 py-2 font-semibold text-center uppercase border border-gray-600 bg-navy-500 text-gray-0">
                                        Control Efficacy
                                    </th>
                                    <th className="px-3 py-2 font-semibold text-center uppercase border border-gray-600 bg-navy-500 text-gray-0">
                                        Risk Score
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {sectionRiskRows.map((row, index) => {
                                    const inherentNum = parseInt(row.inherentRiskScore, 10);
                                    const hasValidInherent = !Number.isNaN(inherentNum) && inherentNum >= 1 && inherentNum <= 10;
                                    const riskScoreColorKey = getSectionRiskScoreColorKey(row.riskScore, hasValidInherent)
                                    const riskScoreBgColor = riskScoreColorKey ? colorPalette[riskScoreColorKey] : null
                                    const riskScoreTextClass = riskScoreColorKey ? getTextColorClass(riskScoreColorKey) : "text-gray-0"

                                    return (
                                        <tr key={`${row.category}-${index}`} className="border-b border-gray-600 last:border-b-0">
                                            <td className="px-3 py-2 font-medium text-left uppercase border border-gray-600 bg-navy-500 text-gray-0">
                                                {row.risk}
                                            </td>
                                            <td className="px-3 py-2 text-left border border-gray-600 text-gray-0 bg-navy-500/40">
                                                {row.category}
                                            </td>
                                            <td className="px-3 py-2 text-center border border-gray-600 bg-navy-500/40 text-gray-0">
                                                {row.inherentRiskScore}
                                            </td>
                                            <td className="px-3 py-2 text-center border border-gray-600 bg-navy-500/40 text-gray-0">
                                                {row.controlEfficacy}
                                            </td>
                                            <td
                                                className={`px-3 py-2 text-center border border-gray-600 bg-navy-500/40 font-semibold ${riskScoreTextClass}`}
                                                style={{ backgroundColor: riskScoreBgColor || undefined }}
                                            >
                                                {row.riskScore}
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>

            <Card className="border-none rounded-md shadow-none bg-navy-100">
                <CardContent className="p-6">
                    <h5 className="mb-4 font-semibold uppercase text-gray-0">Mid Point</h5>
                    <div className="space-y-3">
                        <div className="flex items-center gap-3 p-3 rounded bg-navy-500">
                            <div className="w-8 h-8 rounded" style={{ backgroundColor: colorPalette.red }}></div>
                            <div className="flex-1">
                                <span className="text-gray-0">Poor</span>
                                <span className="ml-3 text-sm text-gray-400">(Fixed Value: 3)</span>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 p-3 rounded bg-navy-500">
                            <div className="w-8 h-8 rounded" style={{ backgroundColor: colorPalette.orange }}></div>
                            <div className="flex-1">
                                <span className="text-gray-0">Limited</span>
                                <span className="ml-3 text-sm text-gray-400">(Fixed Value: 12)</span>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 p-3 rounded bg-navy-500">
                            <div className="w-8 h-8 rounded" style={{ backgroundColor: colorPalette.yellow }}></div>
                            <div className="flex-1">
                                <span className="text-gray-0">Adequate</span>
                                <span className="ml-3 text-sm text-gray-400">(Fixed Value: 28)</span>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 p-3 rounded bg-navy-500">
                            <div className="w-8 h-8 rounded" style={{ backgroundColor: colorPalette.green }}></div>
                            <div className="flex-1">
                                <span className="text-gray-0">Good</span>
                                <span className="ml-3 text-sm text-gray-400">(Fixed Value: 52)</span>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 p-3 rounded bg-navy-500">
                            <div className="w-8 h-8 rounded" style={{ backgroundColor: colorPalette.blue }}></div>
                            <div className="flex-1">
                                <span className="text-gray-0">Excellent</span>
                                <span className="ml-3 text-sm text-gray-400">(Fixed Value: 85)</span>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Card className="border-none rounded-md shadow-none bg-navy-100">
                <CardContent className="p-6">
                    <div className="p-4 overflow-x-auto text-gray-900 bg-yellow-300 border border-gray-600 rounded">
                        <div className="text-base font-bold uppercase">
                            Graph Data - Automatic (From section risk scores)
                        </div>
                        <table className="w-full mt-3 text-sm leading-6">
                            <thead>
                                <tr className="border-b border-gray-600">
                                    <th className="pb-1 font-semibold text-left uppercase">Inherent Risk</th>
                                    <th className="pb-1 font-semibold text-left uppercase">Control Efficacy</th>
                                    <th className="pb-1 font-semibold text-left uppercase">Section</th>
                                    <th className="pb-1 font-semibold text-right uppercase">%</th>
                                </tr>
                            </thead>
                            <tbody>
                                {graphDataRows.map((row) => (
                                    <tr key={row.risk} className="border-b border-gray-500 last:border-b-0">
                                        <td className="py-1">{row.inherent}</td>
                                        <td className="py-1">{row.efficacy}</td>
                                        <td className="py-1">{row.risk}</td>
                                        <td className="py-1 font-semibold text-right">{row.percent}</td>
                                    </tr>
                                ))}
                                {mineOverallRow && (
                                    <tr className="border-t border-gray-700">
                                        <td className="py-1 font-bold">{mineOverallRow.inherent}</td>
                                        <td className="py-1 font-bold">{mineOverallRow.efficacy}</td>
                                        <td className="py-1 font-bold">Mine Overall</td>
                                        <td className="py-1 font-bold text-right">{mineOverallRow.percent}</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>

            <Card className="border-none rounded-md shadow-none bg-navy-100">
                <CardContent className="p-6">
                    <h5 className="mb-4 font-semibold uppercase text-gray-0">Consequence & Rating versus Likelihood Rating</h5>

                    <div className="grid grid-cols-1 gap-4 mb-6 md:grid-cols-2">
                        <div className="p-4 rounded bg-navy-500/80">
                            <p className="text-sm font-semibold uppercase text-gray-0">Consequence Rating</p>
                            <ul className="mt-3 space-y-2 text-sm text-gray-200 list-disc list-inside">
                                <li>Insignificant</li>
                                <li>Minor</li>
                                <li>Moderate</li>
                                <li>Major</li>
                                <li>Severe</li>
                            </ul>
                        </div>
                        <div className="p-4 rounded bg-navy-500/80">
                            <p className="text-sm font-semibold uppercase text-gray-0">Likelihood Rating</p>
                            <ul className="mt-3 space-y-2 text-sm text-gray-200 list-disc list-inside">
                                <li>Certain</li>
                                <li>Likely</li>
                                <li>Possible</li>
                                <li>Unlikely</li>
                                <li>Rare</li>
                            </ul>
                        </div>
                    </div>
                    <div className="mt-4 overflow-x-auto">
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="border-b border-gray-600">
                                    <th className="p-3 text-sm font-semibold text-left uppercase border border-gray-600 bg-navy-500 text-gray-0"></th>
                                    <th
                                        className="p-3 text-sm font-semibold text-center uppercase border border-gray-600 bg-navy-500 text-gray-0"
                                        colSpan={5}
                                    >
                                        Likelihood Rating
                                    </th>
                                </tr>
                                <tr>
                                    <th className="p-3 text-sm font-semibold text-center uppercase border border-gray-600 bg-navy-500 text-gray-0">Consequence Rating</th>
                                    <th className="p-3 text-sm font-semibold uppercase border border-gray-600 bg-navy-500 text-gray-0">Certain</th>
                                    <th className="p-3 text-sm font-semibold uppercase border border-gray-600 bg-navy-500 text-gray-0">Likely</th>
                                    <th className="p-3 text-sm font-semibold uppercase border border-gray-600 bg-navy-500 text-gray-0">Possible</th>
                                    <th className="p-3 text-sm font-semibold uppercase border border-gray-600 bg-navy-500 text-gray-0">Unlikely</th>
                                    <th className="p-3 text-sm font-semibold uppercase border border-gray-600 bg-navy-500 text-gray-0">Rare</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="p-3 text-sm font-medium border border-gray-600 bg-navy-500 text-gray-0">Insignificant</td>
                                    <td className={`p-3 text-sm text-center border border-gray-600 ${getTextColorClass("yellow")}`} style={{ backgroundColor: colorPalette.yellow }}>
                                        <div className="font-semibold">Moderate</div>
                                        <div className="text-xs">6</div>
                                    </td>
                                    <td className={`p-3 text-sm text-center border border-gray-600 ${getTextColorClass("green")}`} style={{ backgroundColor: colorPalette.green }}>
                                        <div className="font-semibold">Minor</div>
                                        <div className="text-xs">7</div>
                                    </td>
                                    <td className={`p-3 text-sm text-center border border-gray-600 ${getTextColorClass("green")}`} style={{ backgroundColor: colorPalette.green }}>
                                        <div className="font-semibold">Minor</div>
                                        <div className="text-xs">8</div>
                                    </td>
                                    <td className={`p-3 text-sm text-center border border-gray-600 ${getTextColorClass("blue")}`} style={{ backgroundColor: colorPalette.blue }}>
                                        <div className="font-semibold">Insignificant</div>
                                        <div className="text-xs">9</div>
                                    </td>
                                    <td className={`p-3 text-sm text-center border border-gray-600 ${getTextColorClass("blue")}`} style={{ backgroundColor: colorPalette.blue }}>
                                        <div className="font-semibold">Insignificant</div>
                                        <div className="text-xs">10</div>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="p-3 text-sm font-medium border border-gray-600 bg-navy-500 text-gray-0">Minor</td>
                                    <td className={`p-3 text-sm text-center border border-gray-600 ${getTextColorClass("yellow")}`} style={{ backgroundColor: colorPalette.yellow }}>
                                        <div className="font-semibold">Moderate</div>
                                        <div className="text-xs">5</div>
                                    </td>
                                    <td className={`p-3 text-sm text-center border border-gray-600 ${getTextColorClass("yellow")}`} style={{ backgroundColor: colorPalette.yellow }}>
                                        <div className="font-semibold">Moderate</div>
                                        <div className="text-xs">5</div>
                                    </td>
                                    <td className={`p-3 text-sm text-center border border-gray-600 ${getTextColorClass("green")}`} style={{ backgroundColor: colorPalette.green }}>
                                        <div className="font-semibold">Minor</div>
                                        <div className="text-xs">7</div>
                                    </td>
                                    <td className={`p-3 text-sm text-center border border-gray-600 ${getTextColorClass("green")}`} style={{ backgroundColor: colorPalette.green }}>
                                        <div className="font-semibold">Minor</div>
                                        <div className="text-xs">8</div>
                                    </td>
                                    <td className={`p-3 text-sm text-center border border-gray-600 ${getTextColorClass("blue")}`} style={{ backgroundColor: colorPalette.blue }}>
                                        <div className="font-semibold">Insignificant</div>
                                        <div className="text-xs">9</div>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="p-3 text-sm font-medium border border-gray-600 bg-navy-500 text-gray-0">Moderate</td>
                                    <td className={`p-3 text-sm text-center border border-gray-600 ${getTextColorClass("orange")}`} style={{ backgroundColor: colorPalette.orange }}>
                                        <div className="font-semibold">Major</div>
                                        <div className="text-xs">3</div>
                                    </td>
                                    <td className={`p-3 text-sm text-center border border-gray-600 ${getTextColorClass("orange")}`} style={{ backgroundColor: colorPalette.orange }}>
                                        <div className="font-semibold">Major</div>
                                        <div className="text-xs">4</div>
                                    </td>
                                    <td className={`p-3 text-sm text-center border border-gray-600 ${getTextColorClass("yellow")}`} style={{ backgroundColor: colorPalette.yellow }}>
                                        <div className="font-semibold">Moderate</div>
                                        <div className="text-xs">6</div>
                                    </td>
                                    <td className={`p-3 text-sm text-center border border-gray-600 ${getTextColorClass("green")}`} style={{ backgroundColor: colorPalette.green }}>
                                        <div className="font-semibold">Minor</div>
                                        <div className="text-xs">7</div>
                                    </td>
                                    <td className={`p-3 text-sm text-center border border-gray-600 ${getTextColorClass("green")}`} style={{ backgroundColor: colorPalette.green }}>
                                        <div className="font-semibold">Minor</div>
                                        <div className="text-xs">8</div>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="p-3 text-sm font-medium border border-gray-600 bg-navy-500 text-gray-0">Major</td>
                                    <td className={`p-3 text-sm text-center border border-gray-600 ${getTextColorClass("red")}`} style={{ backgroundColor: colorPalette.red }}>
                                        <div className="font-semibold">Severe</div>
                                        <div className="text-xs">2</div>
                                    </td>
                                    <td className={`p-3 text-sm text-center border border-gray-600 ${getTextColorClass("orange")}`} style={{ backgroundColor: colorPalette.orange }}>
                                        <div className="font-semibold">Major</div>
                                        <div className="text-xs">3</div>
                                    </td>
                                    <td className={`p-3 text-sm text-center border border-gray-600 ${getTextColorClass("yellow")}`} style={{ backgroundColor: colorPalette.yellow }}>
                                        <div className="font-semibold">Moderate</div>
                                        <div className="text-xs">5</div>
                                    </td>
                                    <td className={`p-3 text-sm text-center border border-gray-600 ${getTextColorClass("yellow")}`} style={{ backgroundColor: colorPalette.yellow }}>
                                        <div className="font-semibold">Moderate</div>
                                        <div className="text-xs">6</div>
                                    </td>
                                    <td className={`p-3 text-sm text-center border border-gray-600 ${getTextColorClass("yellow")}`} style={{ backgroundColor: colorPalette.yellow }}>
                                        <div className="font-semibold">Moderate</div>
                                        <div className="text-xs">6</div>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="p-3 text-sm font-medium border border-gray-600 bg-navy-500 text-gray-0">Severe</td>
                                    <td className={`p-3 text-sm text-center border border-gray-600 ${getTextColorClass("red")}`} style={{ backgroundColor: colorPalette.red }}>
                                        <div className="font-semibold">Severe</div>
                                        <div className="text-xs">1</div>
                                    </td>
                                    <td className={`p-3 text-sm text-center border border-gray-600 ${getTextColorClass("red")}`} style={{ backgroundColor: colorPalette.red }}>
                                        <div className="font-semibold">Severe</div>
                                        <div className="text-xs">2</div>
                                    </td>
                                    <td className={`p-3 text-sm text-center border border-gray-600 ${getTextColorClass("orange")}`} style={{ backgroundColor: colorPalette.orange }}>
                                        <div className="font-semibold">Major</div>
                                        <div className="text-xs">3</div>
                                    </td>
                                    <td className={`p-3 text-sm text-center border border-gray-600 ${getTextColorClass("yellow")}`} style={{ backgroundColor: colorPalette.yellow }}>
                                        <div className="font-semibold">Moderate</div>
                                        <div className="text-xs">5</div>
                                    </td>
                                    <td className={`p-3 text-sm text-center border border-gray-600 ${getTextColorClass("yellow")}`} style={{ backgroundColor: colorPalette.yellow }}>
                                        <div className="font-semibold">Moderate</div>
                                        <div className="text-xs">6</div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>
        </div>
    )
}

export default ReportSummaryTab;
